import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Header bell icon with red unread-count badge + pulse animation.
 * Polls /profile/notifications/unread-count every 45s, also re-fetches
 * on tab focus / route change so admin-pushed messages surface quickly.
 */
export function NotificationBell({ className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/profile/notifications/unread-count");
      setCount(Number(data?.unread_count) || 0);
    } catch {
      /* silent */
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const id = setInterval(fetchCount, 45000); // poll every 45s
    const onFocus = () => fetchCount();
    const onVisible = () => { if (document.visibilityState === "visible") fetchCount(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, fetchCount]);

  if (!user) return null;
  const has = count > 0;

  return (
    <button
      type="button"
      aria-label={has ? `${count} unread notifications` : "notifications"}
      data-testid="notif-bell"
      onClick={() => {
        navigate("/profile/notifications");
        // Optimistically zero the badge — the page will mark items + refresh.
        setTimeout(fetchCount, 1500);
      }}
      className={`relative h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition-all ${
        has
          ? "bg-emerald-500/15 hover:bg-emerald-500/25 ring-1 ring-emerald-400/40"
          : "bg-white/[0.04] hover:bg-white/10 ring-1 ring-white/10 hover:ring-white/25"
      } ${className}`}
    >
      <Bell
        size={16}
        className={`${has ? "text-emerald-300" : "text-white/70"} ${has ? "animate-[wiggle_1.2s_ease-in-out_infinite]" : ""}`}
        strokeWidth={2.2}
      />
      {has && (
        <>
          {/* Pulsing red badge with count */}
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-[#ef4444] to-[#b91c1c] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#0d141d] shadow-[0_2px_8px_rgba(239,68,68,0.55)]"
            data-testid="notif-bell-count"
          >
            {count > 99 ? "99+" : count}
          </span>
          {/* Soft ping ring */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full animate-ping"
            style={{ background: "rgba(239,68,68,0.20)", animationDuration: "2s" }}
          />
        </>
      )}
    </button>
  );
}

export default NotificationBell;
